import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Save, Heart, Sparkles, TrendingUp, Users as UsersIcon, Package } from "lucide-react";
import { toast } from "sonner";

export default function WishlistAdmin() {
  const [s, setS] = useState(null);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/settings"),
      api.get("/admin/wishlist/stats").catch(() => ({ data: null })),
    ]);
    setS(a.data);
    setStats(b.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Configuration favoris enregistrée");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (!s) return <div className="p-10 text-sm text-[hsl(var(--muted-foreground))]">Chargement…</div>;

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="wishlist-admin">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">Boutique</div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Favoris client</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
            Les visiteurs connectés peuvent sauvegarder leurs produits préférés. Configurez une offre automatique qui se déclenche après quelques jours dans les favoris pour booster la conversion.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-navy)]" data-testid="save-wishlist">
          <Save className="w-4 h-4 mr-1.5" />{saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </header>

      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]"><Heart className="w-4 h-4 text-[color:var(--brand-red)]" />Activation</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Affiche les cœurs sur les fiches et cartes produits, active la page "Mes favoris".</div>
          </div>
          <Switch checked={!!s.wishlist_enabled} onCheckedChange={v => setS({ ...s, wishlist_enabled: v })} data-testid="wishlist-enabled" />
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]"><Sparkles className="w-4 h-4 text-[color:var(--brand-red)]" />Offre ciblée "Wishlist Boost"</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))]">
          Un badge coloré est affiché sur la fiche produit si le visiteur a ce produit en favori depuis plus de N jours, pour l'inciter à passer commande.
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Délai avant déclenchement (jours)</Label>
            <Input type="number" min={0} value={s.wishlist_discount_after_days ?? 7} onChange={e => setS({ ...s, wishlist_discount_after_days: +e.target.value })} data-testid="wishlist-delay" />
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">0 = immédiat. Recommandé : 5 à 14 jours.</div>
          </div>
          <div>
            <Label>Pourcentage affiché (%)</Label>
            <Input type="number" min={0} max={50} value={s.wishlist_discount_percent ?? 10} onChange={e => setS({ ...s, wishlist_discount_percent: +e.target.value })} data-testid="wishlist-percent" />
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Purement visuel pour le moment. Le code promo réel sera ajouté ultérieurement.</div>
          </div>
        </div>
        <div>
          <Label>Texte de l'offre</Label>
          <Input value={s.wishlist_discount_text || ""} onChange={e => setS({ ...s, wishlist_discount_text: e.target.value })} placeholder="Offre spéciale pour vous : -{percent}% sur ce produit favori !" />
          <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Le placeholder <code>{"{percent}"}</code> est remplacé automatiquement.</div>
        </div>
      </Card>

      <Card className="p-5 space-y-4" data-testid="wishlist-stats">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]"><TrendingUp className="w-4 h-4" />Statistiques</div>
        {!stats ? <div className="text-sm text-[hsl(var(--muted-foreground))]">Chargement…</div> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-[color:var(--brand-red)]/10 border border-[color:var(--brand-red)]/20">
                <div className="text-[10px] uppercase tracking-wider text-[color:var(--brand-red)] flex items-center gap-1"><Heart className="w-3 h-3" />Favoris totaux</div>
                <div className="text-2xl font-bold text-[color:var(--brand-red)]">{stats.total_favorites || 0}</div>
              </div>
              <div className="p-3 rounded-lg bg-[color:var(--brand-sky)]/10 border border-[color:var(--brand-sky)]/20">
                <div className="text-[10px] uppercase tracking-wider text-[color:var(--brand-sky)] flex items-center gap-1"><UsersIcon className="w-3 h-3" />Clients actifs</div>
                <div className="text-2xl font-bold text-[color:var(--brand-sky)]">{stats.distinct_customers || 0}</div>
              </div>
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 flex items-center gap-1"><Package className="w-3 h-3" />Produits favoris</div>
                <div className="text-2xl font-bold text-emerald-700">{stats.top_products?.length || 0}</div>
              </div>
            </div>
            {stats.top_products?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] mb-2">Top produits favoris</div>
                <div className="space-y-1.5">
                  {stats.top_products.slice(0, 10).map((p, i) => (
                    <div key={p.id} className="flex items-center gap-3 p-2 rounded-lg bg-[hsl(var(--muted))]/40">
                      <span className="font-mono text-[11px] w-6 text-[hsl(var(--muted-foreground))]">#{i + 1}</span>
                      {p.image_url && <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                      <Link to={`/shop/${p.slug}`} target="_blank" className="flex-1 text-sm font-semibold text-[color:var(--brand-navy)] hover:underline truncate">{p.title}</Link>
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-[color:var(--brand-red)]">
                        <Heart className="w-3 h-3 fill-current" />{p.favorited_count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
